import type { RecipePhotoRef } from '@awwyeah/shared';
import { openRecipeOutboxStores } from './idb';
import { RecipePhotoOutbox } from './photoOutbox';
import type { OutboxRecord, OutboxStore, RecipePhotoOutboxRecord } from './store';
import { MemoryOutboxStore } from './store';

export type { RecipePhotoOutboxRecord } from './store';
export { RecipePhotoOutbox } from './photoOutbox';

/**
 * The recipe module's single photo outbox, started on boot — same "enqueue instantly, land
 * once IndexedDB (or memory) resolves" shape as check-in's `getCheckinOutbox`.
 */

class LazyOutboxStore<T extends OutboxRecord> implements OutboxStore<T> {
  constructor(private readonly resolved: Promise<OutboxStore<T>>) {}
  async put(record: T): Promise<void> {
    return (await this.resolved).put(record);
  }
  async get(id: string): Promise<T | undefined> {
    return (await this.resolved).get(id);
  }
  async all(): Promise<T[]> {
    return (await this.resolved).all();
  }
  async delete(id: string): Promise<void> {
    return (await this.resolved).delete(id);
  }
}

const uploadedHandlers = new Set<(record: RecipePhotoOutboxRecord) => void>();
const uploadingHandlers = new Set<(record: RecipePhotoOutboxRecord) => void>();
const changeListeners = new Set<() => void>();

let outbox: RecipePhotoOutbox | null = null;
let pendingSnapshot: RecipePhotoOutboxRecord[] = [];

function notifyChanged(): void {
  void outbox?.pending().then((records) => {
    const changed =
      records.length !== pendingSnapshot.length ||
      records.some((record, index) => pendingSnapshot[index]?.id !== record.id);
    if (!changed) return;
    pendingSnapshot = records;
    for (const listener of changeListeners) listener();
  });
}

export function getRecipePhotoOutbox(): RecipePhotoOutbox {
  if (outbox) return outbox;

  const stores = openRecipeOutboxStores();
  const photos = new LazyOutboxStore<RecipePhotoOutboxRecord>(
    stores.then((s) => s?.photos ?? new MemoryOutboxStore<RecipePhotoOutboxRecord>()),
  );

  outbox = new RecipePhotoOutbox({
    photos,
    onPhotoUploaded: (record) => {
      for (const handler of uploadedHandlers) handler(record);
      notifyChanged();
    },
    onPhotoUploading: (record) => {
      for (const handler of uploadingHandlers) handler(record);
    },
    onChange: notifyChanged,
  });
  outbox.start();
  outbox.listenForOnline();
  notifyChanged();
  return outbox;
}

/** Test/teardown seam. */
export function resetRecipePhotoOutboxForTests(): void {
  outbox?.stop();
  outbox = null;
  pendingSnapshot = [];
  uploadedHandlers.clear();
  uploadingHandlers.clear();
  changeListeners.clear();
}

export function subscribeToRecipePhotoOutbox(listener: () => void): () => void {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

export function pendingRecipePhotosSnapshot(): RecipePhotoOutboxRecord[] {
  return pendingSnapshot;
}

/** Fires when bytes reach the server — the module flips `photoRef.state` in Yjs here. */
export function onRecipePhotoUploaded(
  handler: (record: RecipePhotoOutboxRecord) => void,
): () => void {
  uploadedHandlers.add(handler);
  return () => uploadedHandlers.delete(handler);
}

/** The photo bytes for a recipe still waiting on this device, if we have them. */
export async function localRecipePhotoBlob(photoId: string): Promise<Blob | null> {
  const record = pendingSnapshot.find((entry) => entry.id === photoId);
  if (record) return record.blob;
  const found = await getRecipePhotoOutbox()
    .pending()
    .then((records) => records.find((entry) => entry.id === photoId));
  return found?.blob ?? null;
}

/** `photoRef` for bytes that are now on the server. */
export function uploadedRecipePhotoRef(ref: RecipePhotoRef): RecipePhotoRef {
  return { ...ref, state: 'uploaded' };
}
